<script lang="ts" setup>
	import {useFilters} from '@/store/filter.store';
	import {computed} from 'vue';

	const filterStore = useFilters();

	const emit = defineEmits(['pagination-updated']);

	const defaultPageSize = 20;
	const sizes = [10, 20, 30, 100];

	const activePage = computed(() => filterStore.pagination?.activePage ?? 0);
	const totalPages = computed(() => filterStore.pagination?.totalPages ?? 1);
	const pageSize = computed((): number => filterStore.pagination?.pageSize ?? defaultPageSize);

	const scrollToTop = () => {
		window.scrollTo({behavior: 'smooth', top: 0, left: 0});
	};

	const setActivePage = (page: number): void => {
		filterStore.setActivePage(page);
		emit('pagination-updated');

		scrollToTop();
	};

	const showPage = (page: number): boolean => {
		// If there are less then 11 pages, always show them all.
		if (totalPages.value < 11) {
			return true;
		}

		// Always show first page.
		if (page === 0) {
			return true;
		}

		// Always show last page
		if (page === totalPages.value) {
			return true;
		}

		// Show (max) 7 pages before and after the active page
		return Math.abs(page - activePage.value) < 6;
	};

	const pageClasses = (page: number): { pagination__current: boolean } => {
		return {
			'pagination__current': page - 1 === activePage.value
		};
	};

	const showPageBreak = (page: number): boolean => {
		// Never show after the last page
		if (page === totalPages.value) {
			return false;
		}

		// Show 'break' if the next page is not visible in the pagination,
		// so for example we get: 1  - 2 - 3 ... 4  - 5 etc.
		return !showPage(page + 1);
	};

	const updatePageSize = (event: Event): void => {
		if (!event) {
			return;
		}

		const element = event.target as HTMLInputElement;
		const newPageSize = parseInt(element.value);

		filterStore.setPageSize(newPageSize);
		emit('pagination-updated');

		scrollToTop();
	};

</script>

<template>
	<div class="catalog-overview-footer">
		<ul v-if="filterStore.pagination" class="pagination">
			<li v-if="activePage > 0" class="pagination__prev">
				<a href="#" @click.stop.prevent="setActivePage(activePage - 1)">
					{{ $t('site.products.overview.pagination.button.previous.text') }}
				</a>
			</li>

			<template v-if="totalPages > 0">
				<!-- totalPages is zero based, so we should add one -->
				<template v-for="page in totalPages">
					<li v-if="showPage(page)" :key="`page-${page}`" :class="pageClasses(page)">
						<span v-if="(page - 1) === activePage">{{ page }}</span>
						<a v-else href="#" @click.stop.prevent="setActivePage(page - 1)">
							<span>{{ page }}</span>
						</a>
					</li>

					<template v-if="showPage(page)">&nbsp;</template>

					<li v-if="showPage(page) && showPageBreak(page)"
					    :key="`pagebreak-${page}`">
						<span>&hellip;</span>
					</li>
				</template>
			</template>

			<!-- activePage is zero based, totalPages is not.-->
			<li v-if="(activePage + 1) < totalPages" class="pagination__next">
				<a href="#" @click.stop.prevent="setActivePage(activePage + 1)">
					{{ $t('site.products.overview.pagination.button.next.text') }}
				</a>
			</li>
		</ul>

		<div v-if="filterStore.pagination" class="results-per-page">
			<label>
				{{ $t('site.products.overview.pagination.button.pagesize.text') }}
			</label>

			<select
					id="pageSizeSelector"
					:value="pageSize"
					@change="updatePageSize($event)">
				<option v-for="size in sizes" :key="size" :value="size">{{ size }}</option>
			</select>
		</div>
	</div>
</template>
